import React from 'react';
import { Button, Badge, SpaceBetween, Toggle, Grid, Box } from '@amzn/awsui-components-core';
import { generateProfilePictureUrl, generatePhoneToolUrl } from '../../../../../constants/AppConstants';
import { ResolvedMatch } from '../../interfaces/interface';
import { Employee } from '../../interfaces/interface';
import { ParticipantSelector } from './ParticipantSelector';
import { EmployeeListItem } from './EmployeeListItem';
import { ProfilePicture } from '../../ui/ProfilePicture';
import './HoverStyle.scss';
import './AttendeeCard.scss';

interface AttendeeCardProps {
  attendee: ResolvedMatch;
  onUpdate: (id: string, updates: Partial<ResolvedMatch>) => void;
  onRemove: (id: string) => void;
  isDuplicate?: boolean;
  existingEmails?: Set<string>;
}

export const AttendeeCard: React.FC<AttendeeCardProps> = ({ attendee, onUpdate, onRemove, isDuplicate = false, existingEmails = new Set() }) => {
  const handleEmployeeSelect = (employee: Employee) => {
    onUpdate(attendee.id, {
      selectedEmployee: employee,
      resolvedEmail: employee.employee_internal_email_address,
      editedName: employee.employee_name,
      status: 'manual'
    });
  };

  const handlePriorityToggle = ({ detail }: { detail: { checked: boolean } }) => {
    const newPriority = detail.checked ? 'required' : 'optional';
    onUpdate(attendee.id, { priority: newPriority });
  };

  const displayName = attendee.editedName || attendee.selectedEmployee?.employee_name || attendee.inputName;
  const loginName = attendee.selectedEmployee?.login_name || '';
  const email = attendee.resolvedEmail || attendee.manualEmail || '';

  // Create a display employee object for EmployeeListItem
  const displayEmployee: Employee | undefined = attendee.selectedEmployee
    ? {
        ...attendee.selectedEmployee,
        employee_name: displayName,
        displayName: displayName
      }
    : undefined;

  return (
    <div className="attendee-card-wrapper hoverable-row">
      <Grid
        gridDefinition={[
          { colspan: 5 }, // Name, Email and Details + Manual Search (equal width)
          { colspan: 5 }, // Executive Assistant Details (equal width)
          { colspan: 1 }, // Priority Toggle (compact)
          { colspan: 1 } // Action Buttons (compact)
        ]}
      >
        {/* Column 1: Profile Picture + Name, Email and Details (no checkbox) */}
        <div className="attendee-column">
          {/* Main horizontal row: Profile Picture + Name + Select Participant */}
          {displayEmployee ? (
            <EmployeeListItem
              employee={displayEmployee}
              showDetails={!!(email && attendee.selectedEmployee)}
              disableHover={true}
              onClick={() => {
                if (loginName) {
                  window.open(generatePhoneToolUrl(loginName), '_blank');
                }
              }}
            />
          ) : (
            <div className="employee-item employee-item--unresolved">
              <div className="employee-info-container">
                <ProfilePicture loginName="" name={displayName} size="small" />
                <div className="employee-name-wrapper">
                  <div className="employee-name-line">
                    <span className="employee-name-text">{displayName}</span>
                  </div>
                  {email && <div className="employee-details-text">{email}</div>}
                </div>
              </div>
              {/* Participant Selector - for unresolved attendees, positioned as action */}
              <div className="employee-item-actions">
                <ParticipantSelector
                  onEmployeeSelect={handleEmployeeSelect}
                  placeholder={`Search for "${attendee.inputName}"...`}
                  initialQuery={attendee.inputName}
                  existingEmails={existingEmails}
                />
              </div>
            </div>
          )}

          {/* Error/Warning Messages */}
          {(isDuplicate || attendee.hasError) && (
            <Box margin={{ top: 'xxxs' }}>
              {isDuplicate && <Badge color="red">⚠️ Duplicate</Badge>}
              {attendee.hasError && <Badge color="red">❌ {attendee.errorMessage || 'Not found'}</Badge>}
            </Box>
          )}
        </div>

        {/* Column 3: Executive Assistant Details */}
        <div className="attendee-column">
          {attendee.selectedEmployee ? (
            attendee.selectedExecutiveAssistant ? (
              // Show the executive assistant found by useAttendeeManagement
              <EmployeeListItem
                employee={attendee.selectedExecutiveAssistant}
                showDetails={!!attendee.selectedExecutiveAssistant.employee_internal_email_address}
                disableHover={true}
                onClick={() => {
                  window.open(generatePhoneToolUrl(attendee.selectedExecutiveAssistant.login_name), '_blank');
                }}
              />
            ) : (
              // No executive assistant found
              <div className="employee-item">
                <div className="employee-info-container">
                  <div style={{ width: '32px', height: '32px' }} /> {/* Placeholder for profile picture space */}
                  <div className="employee-name-wrapper">
                    <Box fontSize="body-s" color="text-body-secondary">
                      <em>-</em>
                    </Box>
                  </div>
                </div>
              </div>
            )
          ) : (
            // No selected employee
            <div className="employee-item">
              <div className="employee-info-container">
                <div style={{ width: '32px', height: '32px' }} /> {/* Placeholder for profile picture space */}
                <div className="employee-name-wrapper">
                  <Box fontSize="body-s" color="text-status-inactive">
                    <em>-</em>
                  </Box>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Column 4: Priority Toggle */}
        <Box padding={{ vertical: 'xxs' }} textAlign="center">
          <Toggle onChange={handlePriorityToggle} checked={attendee.priority === 'required'}>
            <span className="priority-toggle-text">{attendee.priority === 'required' ? 'Required' : 'Optional'}</span>
          </Toggle>
        </Box>

        {/* Column 5: Action Buttons */}
        <Box padding={{ vertical: 'xxs' }} textAlign="center">
          <Button variant="normal" onClick={() => onRemove(attendee.id)} iconName="remove" ariaLabel="Delete attendee" />
        </Box>
      </Grid>
    </div>
  );
};
